import React, { useEffect, useMemo, useState, useRef } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Plus, X, Download, Save, CheckCircle2, AlertTriangle, Search, Lightbulb, ArrowUp, ArrowDown, ListPlus, Users } from "lucide-react";

const ONDERDEEL_KEYS = { groepspringen: "groepspringen", "micro-meso": "micro_meso", individueel: "individueel" };
const TOESTEL_OPTIONS = [
  { v: "mt", l: "Mini-trampoline" },
  { v: "pegasus", l: "Pegasus" },
  { v: "airtrack", l: "Airtrack" },
];
const MICRO_TOESTEL_OPTIONS = [
  { v: "mt", l: "Mini-trampoline (incl. pegasus)" },
  { v: "airtrack", l: "Airtrack" },
];

const TEMPLATE_BY_ONDERDEEL = {
  groepspringen: { mt: "groepspringen_6_sprongen", pegasus: "groepspringen_6_sprongen", airtrack: "groepspringen_airtrack" },
  micro_meso: { mt: "micro_mt", pegasus: "micro_mt", airtrack: "micro_airtrack" },
  individueel: { mt: "individueel_mt", pegasus: "individueel_mt", airtrack: "individueel_airtrack" },
};

export default function SprongChecker({ onderdeel: routeOnderdeel }) {
  const onderdeel = ONDERDEEL_KEYS[routeOnderdeel] || routeOnderdeel;
  const { user } = useAuth();
  const location = useLocation();
  const [waarden, setWaarden] = useState(null);
  const [leeftijd, setLeeftijd] = useState("");
  const [niveau, setNiveau] = useState("");
  const [toestel, setToestel] = useState("mt");
  const [vereniging, setVereniging] = useState("");
  const [team, setTeam] = useState("");
  const [sprongen, setSprongen] = useState([]);
  const [series, setSeries] = useState([[], []]); // voor airtrack: 2 series default
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showOwn, setShowOwn] = useState(false);
  const [ownForm, setOwnForm] = useState({ naam: "", d: "" });
  const usesSeries = toestel === "airtrack" && onderdeel !== "micro_meso" && onderdeel !== "groepspringen";
  const isTop3 = niveau === "A" || niveau === "A-ere";
  const isMicro = onderdeel === "micro_meso" && leeftijd.startsWith("micro_");
  const isGroepsTeam = onderdeel === "groepspringen";

  useEffect(() => {
    api.get("/reglement/waarden").then((r) => {
      setWaarden(r.data);
      const block = r.data[onderdeel];
      if (block) {
        const firstL = Object.keys(block.leeftijden)[0];
        setLeeftijd(firstL);
        const firstN = Object.keys(block.leeftijden[firstL].niveaus)[0];
        setNiveau(firstN);
      }
    });
  }, [onderdeel]);

  // Pas toestel-defaults aan voor Micro (alleen mt/airtrack)
  useEffect(() => {
    if (isMicro && toestel === "pegasus") setToestel("mt");
  }, [isMicro, toestel]);

  // Pre-fill vanuit "Mijn sprongen → Openen"
  useEffect(() => {
    const pre = location.state?.prefill;
    if (!pre) return;
    if (pre.leeftijd) setLeeftijd(pre.leeftijd);
    if (pre.niveau) setNiveau(pre.niveau);
    if (pre.toestel) setToestel(pre.toestel);
    const sprongLijst = (pre.sprongen || []).map(s => ({ input: s.naam, naam: s.naam, d: s.d, fuzzy: false }));
    if (pre.toestel === "airtrack") {
      // Verdeel zo gelijk mogelijk over 2 series
      const mid = Math.ceil(sprongLijst.length / 2);
      setSeries([sprongLijst.slice(0, mid).map(s => ({ naam: s.naam, d: s.d })), sprongLijst.slice(mid).map(s => ({ naam: s.naam, d: s.d }))]);
    } else {
      setSprongen(sprongLijst);
    }
    // Reset state zodat refresh niet opnieuw prefilled
    window.history.replaceState({}, "");
  }, [location.state]);

  const leeftijdOptions = useMemo(() => waarden ? Object.entries(waarden[onderdeel].leeftijden).map(([k, v]) => ({ v: k, l: v.label })) : [], [waarden, onderdeel]);
  const niveauOptions = useMemo(() => waarden && leeftijd ? Object.keys(waarden[onderdeel].leeftijden[leeftijd].niveaus) : [], [waarden, onderdeel, leeftijd]);

  const removeSprong = (i) => setSprongen(sprongen.filter((_, idx) => idx !== i));
  const moveSprong = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sprongen.length) return;
    const next = [...sprongen];
    [next[i], next[j]] = [next[j], next[i]];
    setSprongen(next);
  };
  const addOwn = (e) => {
    e?.preventDefault();
    if (!ownForm.naam || !ownForm.d) return;
    setSprongen([...sprongen, { input: ownForm.naam, naam: ownForm.naam + " (eigen)", d: parseFloat(ownForm.d), fuzzy: false }]);
    setOwnForm({ naam: "", d: "" }); setShowOwn(false);
  };

  const addToSerie = (sIdx, sprong) => {
    const next = [...series];
    next[sIdx] = [...next[sIdx], sprong];
    setSeries(next);
  };
  const removeFromSerie = (sIdx, idx) => {
    const next = [...series];
    next[sIdx] = next[sIdx].filter((_, i) => i !== idx);
    setSeries(next);
  };
  const addSerie = () => setSeries([...series, []]);
  const removeSerie = (sIdx) => setSeries(series.filter((_, i) => i !== sIdx));

  const validate = async () => {
    setValidating(true);
    try {
      if (usesSeries) {
        const r = await api.post("/checker/validate-series", {
          onderdeel, leeftijd, niveau, toestel,
          series: series.map(s => s.map(x => ({ naam: x.naam, d: x.d }))),
        });
        setResult({ ...r.data, mode: "series" });
      } else {
        const r = await api.post("/checker/validate", {
          onderdeel, leeftijd, niveau, toestel,
          sprongen: sprongen.map((s) => s.naam || s.input),
        });
        setResult({ ...r.data, mode: "single" });
      }
    } catch (e) {
      setResult({ error: formatError(e) });
    } finally { setValidating(false); }
  };

  const loadSuggestions = async () => {
    const currentTotal = usesSeries
      ? series.reduce((s, serie) => s + serie.reduce((x, sp) => x + (sp.d || 0), 0), 0)
      : sprongen.reduce((s, x) => s + (x.d || 0), 0);
    try {
      const r = await api.post("/checker/suggest-next", {
        onderdeel, leeftijd, niveau, toestel, huidig_total: currentTotal, aantal_aanbevolen: 5,
      });
      setSuggestions(r.data.suggestions || []);
    } catch { setSuggestions([]); }
  };

  const downloadExcel = async () => {
    const tpl = TEMPLATE_BY_ONDERDEEL[onderdeel]?.[toestel] || "groepspringen_6_sprongen";
    const r = await api.post("/excel/generate", {
      template: tpl, vereniging, team, niveau, leeftijd,
      sprongen: sprongen.filter(s => s.naam).map(s => ({ naam: s.naam, d: s.d })),
    }, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `sprongformulier_${tpl}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const onderdeelLabel = waarden?.[onderdeel]?.label || onderdeel;
    const leeftijdLabel = waarden?.[onderdeel]?.leeftijden?.[leeftijd]?.label || leeftijd;
    const r = await api.post("/pdf/generate", {
      onderdeel: onderdeelLabel, vereniging, team, niveau, leeftijd: leeftijdLabel, toestel,
      sprongen: sprongen.filter(s => s.naam).map(s => ({ naam: s.naam, d: s.d })),
    }, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `sprongformulier_${onderdeel}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const saveRoutine = async () => {
    if (!user) return;
    try {
      await api.post("/routines", {
        titel: `${onderdeel} - ${niveau} - ${toestel} - ${new Date().toLocaleDateString()}`,
        onderdeel, leeftijd, niveau, toestel,
        sprongen: sprongen.map(s => ({ naam: s.naam, d: s.d, input: s.input })),
      });
      setSaveMsg("Opgeslagen!");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch (e) { setSaveMsg(formatError(e)); }
  };

  if (!waarden) return <div className="text-slate-500">Laden...</div>;
  const limits = waarden[onderdeel].leeftijden[leeftijd]?.niveaus?.[niveau]?.[toestel] || {};

  // ===== MICRO MODE =====
  if (isMicro) {
    return (
      <MicroChecker
        leeftijd={leeftijd}
        setLeeftijd={setLeeftijd}
        niveau={niveau}
        setNiveau={setNiveau}
        toestel={toestel}
        setToestel={setToestel}
        vereniging={vereniging}
        setVereniging={setVereniging}
        team={team}
        setTeam={setTeam}
        leeftijdOptions={leeftijdOptions}
        niveauOptions={niveauOptions}
        limits={limits}
      />
    );
  }

  // ===== GROEPSPRINGEN TEAM-MODUS =====
  if (isGroepsTeam) {
    return (
      <GroepspringenTeamChecker
        leeftijd={leeftijd} setLeeftijd={setLeeftijd}
        niveau={niveau} setNiveau={setNiveau}
        toestel={toestel} setToestel={setToestel}
        vereniging={vereniging} setVereniging={setVereniging}
        team={team} setTeam={setTeam}
        leeftijdOptions={leeftijdOptions} niveauOptions={niveauOptions}
        limits={limits}
      />
    );
  }

  return (
    <div data-testid={`checker-${onderdeel}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Tool</div>
          <h1 className="heading-display text-4xl sm:text-5xl mt-1">Sprong Checker</h1>
        </div>
        {onderdeel === "groepspringen" && null}
      </div>
      <p className="text-slate-600 mt-3 mb-8">Voeg je sprongen toe (afkortingen mogen!) en check direct of ze passen binnen jouw niveau.</p>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="gs-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Leeftijd" value={leeftijd} onChange={setLeeftijd} opts={leeftijdOptions} testid="select-leeftijd"/>
            <Select label="Niveau" value={niveau} onChange={setNiveau} opts={niveauOptions.map(n => ({v:n,l:n}))} testid="select-niveau"/>
          </div>
          <Select label="Toestel" value={toestel} onChange={setToestel} opts={TOESTEL_OPTIONS} testid="select-toestel"/>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Vereniging" value={vereniging} onChange={setVereniging} testid="input-vereniging"/>
            <TextField label="Team / turner" value={team} onChange={setTeam} testid="input-team"/>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div className="text-slate-600 mb-1">Totaal-som moet zijn:</div>
            <div className="font-mono font-semibold" data-testid="limit-display">
              {limits.geen_max && "Geen maximum"}
              {!limits.geen_max && limits.min != null && limits.max != null && `tussen ${limits.min} en ${limits.max}`}
              {!limits.geen_max && limits.min != null && limits.max == null && `minimaal ${limits.min}`}
              {!limits.geen_max && limits.min == null && limits.max != null && `maximaal ${limits.max}`}
              {!limits.geen_max && limits.min == null && limits.max == null && "geen limiet voor dit niveau"}
            </div>
          </div>
        </div>

        <div className="gs-card p-5">
          <div className="text-sm font-medium text-slate-700 mb-2">{usesSeries ? "Sprong toevoegen aan serie" : "Sprong toevoegen"}</div>
          {!usesSeries && <SprongInput toestel={toestel} onAdd={(sprong) => setSprongen([...sprongen, sprong])}/>}
          {usesSeries && <div className="text-sm text-slate-500">Gebruik de invoer per serie hieronder.</div>}
          <div className="text-xs text-slate-500 mt-2">Tip: typ afkortingen zoals 'dub hoek' of 'sas ½' — suggesties verschijnen automatisch.</div>
        </div>
      </div>

      <div className="gs-card p-5 mb-6">
        {usesSeries ? (
          /* SERIES MODE — airtrack */
          <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-bold">Series ({series.length})</h2>
              <div className="flex items-center gap-3 text-sm">
                {isTop3 && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">A/A-ere: alleen top-3 telt</span>}
                <button onClick={addSerie} className="text-orange-600 hover:text-orange-700 font-semibold inline-flex items-center gap-1" data-testid="add-serie-btn"><ListPlus size={14}/> Serie toevoegen</button>
              </div>
            </div>
            {series.map((serie, sIdx) => {
              const total = serie.reduce((s, x) => s + (x.d || 0), 0);
              const counted = isTop3 ? [...serie].map(x => x.d).sort((a,b)=>b-a).slice(0,3).reduce((s,x)=>s+x,0) : total;
              return (
                <div key={sIdx} className="border-l-4 border-orange-300 pl-4 mb-4" data-testid={`serie-${sIdx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold">Serie {sIdx + 1} <span className="text-sm font-normal text-slate-500">({serie.length} elementen — min 3, max 5)</span></div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">D: <span className="font-mono font-bold">{counted.toFixed(1)}</span>{isTop3 && total !== counted && <span className="text-slate-400 text-xs ml-1">(top-3 van {total.toFixed(1)})</span>}</span>
                      {series.length > 1 && <button onClick={() => removeSerie(sIdx)} className="text-slate-400 hover:text-red-600" data-testid={`remove-serie-${sIdx}`}><X size={16}/></button>}
                    </div>
                  </div>
                  <ul className="space-y-1 mb-2">
                    {serie.map((sp, i) => (
                      <li key={i} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5 text-sm" data-testid={`serie-${sIdx}-sprong-${i}`}>
                        <span className="flex-1">{sp.naam}</span>
                        <span className="font-mono">{sp.d?.toFixed(1)}</span>
                        <button onClick={() => removeFromSerie(sIdx, i)} className="text-slate-400 hover:text-red-600"><X size={14}/></button>
                      </li>
                    ))}
                    {serie.length === 0 && <li className="text-slate-400 text-sm italic py-2">Voeg sprongen toe...</li>}
                  </ul>
                  <SerieSprongPicker toestel={toestel} onAdd={(s) => addToSerie(sIdx, s)}/>
                </div>
              );
            })}
          </>
        ) : (
          /* SINGLE LIST MODE — mt/pegasus */
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">Sprongen ({sprongen.length})</h2>
              <div className="text-sm text-slate-600">Totaal D: <span className="font-mono font-bold">{sprongen.reduce((s, x) => s + (x.d || 0), 0).toFixed(2)}</span></div>
            </div>
            {sprongen.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center" data-testid="no-sprongen-msg">Nog geen sprongen. Voeg er een toe hierboven.</div>
            ) : (
              <ul className="space-y-2" data-testid="sprongen-list">
                {sprongen.map((s, i) => (
                  <li key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`sprong-item-${i}`}>
                    <div className="flex items-center gap-1 mr-2">
                      <button onClick={() => moveSprong(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5" data-testid={`up-${i}`}><ArrowUp size={14}/></button>
                      <button onClick={() => moveSprong(i, 1)} disabled={i === sprongen.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5" data-testid={`down-${i}`}><ArrowDown size={14}/></button>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{s.naam || <span className="text-red-600">Niet herkend</span>}</div>
                      <div className="text-xs text-slate-500">Input: "{s.input}" {s.fuzzy && <span className="ml-1 text-amber-600">(fuzzy)</span>}</div>
                    </div>
                    <div className="font-mono font-bold mr-3">{s.d != null ? s.d.toFixed(1) : "—"}</div>
                    <button onClick={() => removeSprong(i)} className="text-slate-400 hover:text-red-600" data-testid={`remove-sprong-${i}`}><X size={18}/></button>
                  </li>
                ))}
              </ul>
            )}

            {showOwn && (
              <form onSubmit={addOwn} className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2" data-testid="own-sprong-form">
                <input required placeholder="Naam eigen sprong" value={ownForm.naam} onChange={e => setOwnForm({...ownForm, naam: e.target.value})} className="flex-1 px-3 py-2 border rounded" data-testid="own-naam"/>
                <input required type="number" step="0.1" placeholder="D-waarde" value={ownForm.d} onChange={e => setOwnForm({...ownForm, d: e.target.value})} className="w-24 px-3 py-2 border rounded" data-testid="own-d"/>
                <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded font-semibold" data-testid="own-submit">Voeg toe</button>
                <button type="button" onClick={() => setShowOwn(false)} className="text-slate-500 px-2">Annuleer</button>
              </form>
            )}
          </>
        )}

        <div className="flex flex-wrap gap-3 mt-4">
          <button onClick={validate} disabled={validating} className="bg-secondary text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-slate-800" data-testid="validate-btn">
            {validating ? "Controleren..." : "Controleer"}
          </button>
          {!usesSeries && (
            <>
              <button onClick={downloadExcel} disabled={sprongen.length === 0} className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-emerald-700 flex items-center gap-2" data-testid="excel-download-btn">
                <Download size={16}/> Excel
              </button>
              <button onClick={downloadPdf} disabled={sprongen.length === 0} className="bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-red-700 flex items-center gap-2" data-testid="pdf-download-btn">
                <Download size={16}/> PDF
              </button>
              <button onClick={() => setShowOwn(!showOwn)} className="border-2 border-amber-400 text-amber-700 hover:bg-amber-50 px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2" data-testid="add-own-btn">
                + Eigen sprong
              </button>
            </>
          )}
          <button onClick={loadSuggestions} className="border-2 border-purple-400 text-purple-700 hover:bg-purple-50 px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2" data-testid="suggest-btn">
            <Lightbulb size={16}/> Suggesties
          </button>
          {user && !usesSeries && (
            <button onClick={saveRoutine} disabled={sprongen.length === 0} className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="save-routine-btn">
              <Save size={16}/> Opslaan
            </button>
          )}
          {saveMsg && <div className="self-center text-sm text-emerald-600 font-medium" data-testid="save-msg">{saveMsg}</div>}
        </div>

        {suggestions.length > 0 && (
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg" data-testid="suggestions-panel">
            <div className="font-semibold text-purple-900 mb-2 flex items-center gap-2"><Lightbulb size={16}/> Aanbevolen sprongen om binnen niveau te komen:</div>
            <ul className="space-y-1">
              {suggestions.map((s, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span>{s.naam}</span>
                  <span className="font-mono text-purple-700">{s.d.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {result && !result.error && (
        <div className="gs-card p-5" data-testid="validation-result">
          <div className="flex items-center gap-3 mb-4">
            {result.all_ok ? (
              <><CheckCircle2 className="text-emerald-600" size={28}/><div><div className="font-bold text-emerald-700">Past binnen {niveau}-niveau!</div><div className="text-sm text-slate-600">{result.mode === "series" ? `Grand totaal D: ${result.grand_total_d}` : `Totaal D: ${result.total_d}`}</div></div></>
            ) : (
              <><AlertTriangle className="text-amber-600" size={28}/><div><div className="font-bold text-amber-700">Past niet binnen {niveau}-niveau</div><div className="text-sm text-slate-600">{result.total_reason}</div></div></>
            )}
          </div>

          {/* Series-modus details */}
          {result.mode === "series" && result.series && (
            <div className="mb-4 space-y-3">
              {result.top3_applied && (
                <div className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 inline-block font-semibold">Top-3 regel actief (A/A-ere)</div>
              )}
              {result.series.map((s) => (
                <div key={s.index} className={`p-3 rounded-lg border ${s.elem_ok ? "bg-slate-50 border-slate-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">Serie {s.index} <span className="text-sm font-normal text-slate-500">({s.elementen} elementen)</span></span>
                    <span className="font-mono font-bold">D: {s.totaal_d}</span>
                  </div>
                  {!s.elem_ok && <div className="text-xs text-red-700 font-semibold">⚠ {s.elem_reason}</div>}
                  {s.top3_applied && s.elementen > 3 && <div className="text-xs text-purple-700">Top-{s.getelde_sprongen} van {s.elementen} elementen geteld</div>}
                </div>
              ))}
              <div className={`p-3 rounded-lg border-2 ${result.total_ok ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold">Grand Totaal</span>
                  <span className="text-2xl font-mono font-bold">{result.grand_total_d}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {result.min_required != null && `min ${result.min_required}`}
                  {result.min_required != null && result.max_allowed != null && " · "}
                  {result.max_allowed != null && `max ${result.max_allowed}`}
                </div>
              </div>
            </div>
          )}

          {/* Single mode totaal */}
          {result.mode === "single" && (result.min_required != null || result.max_allowed != null) && (
            <div className={`mb-4 p-3 rounded-lg border ${result.total_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="total-check">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-600">Totaal D-waarde</div>
                  <div className={`text-2xl font-bold ${result.total_ok ? "text-emerald-700" : "text-red-700"}`}>
                    {result.total_d}
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      {result.min_required != null && `min ${result.min_required}`}
                      {result.min_required != null && result.max_allowed != null && " · "}
                      {result.max_allowed != null && `max ${result.max_allowed}`}
                    </span>
                  </div>
                </div>
                <div className={`text-3xl ${result.total_ok ? "text-emerald-600" : "text-red-600"}`}>
                  {result.total_ok ? "✓" : "✗"}
                </div>
              </div>
            </div>
          )}

          {result.mode === "single" && result.sprongen && (
            <ul className="space-y-2">
              {result.sprongen.map((s, i) => (
                <li key={i} className={`flex items-center gap-3 p-2 rounded ${s.ok ? "bg-slate-50" : "bg-red-50"}`} data-testid={`result-item-${i}`}>
                  <span className={s.ok ? "text-emerald-600" : "text-red-600"}>{s.ok ? "✓" : "✗"}</span>
                  <span className="font-medium flex-1">{s.naam || s.input}</span>
                  <span className="font-mono">{s.d != null ? s.d : "—"}</span>
                  {s.reason && <span className="text-xs text-red-700 font-semibold">{s.reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {result?.error && <div className="text-red-600">{result.error}</div>}
    </div>
  );
}

function Select({ label, value, onChange, opts, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-input rounded-lg bg-white" data-testid={testid}>
        {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-input rounded-lg bg-white" data-testid={testid}/>
    </label>
  );
}

function SerieSprongPicker({ toestel, onAdd }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ naam: "", d: "" });

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get("/checker/suggest", { params: { query: q, toestel, limit: 6 } }); setSuggestions(r.data); }
      catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [q, toestel]);

  const pick = (s) => { onAdd({ naam: s.naam, d: s.d }); setQ(""); setSuggestions([]); setOpen(false); };
  const submitCustom = (e) => {
    e.preventDefault();
    if (!custom.naam || !custom.d) return;
    onAdd({ naam: custom.naam + " (eigen)", d: parseFloat(custom.d) });
    setCustom({ naam: "", d: "" }); setShowCustom(false);
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder="Voeg element toe..."
            className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"/>
        </div>
        <button type="button" onClick={() => setShowCustom(!showCustom)} className="text-orange-600 border border-orange-300 hover:bg-orange-50 px-3 py-1.5 text-xs rounded-lg font-semibold">+ Eigen</button>
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <li key={s.naam} onMouseDown={(e) => { e.preventDefault(); pick(s); }} className="flex justify-between px-3 py-2 cursor-pointer hover:bg-orange-50 border-b last:border-0 text-sm">
              <span>{s.naam}</span><span className="font-mono text-slate-500">{s.d?.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      )}
      {showCustom && (
        <form onSubmit={submitCustom} className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded flex gap-2">
          <input required placeholder="Naam" value={custom.naam} onChange={e => setCustom({...custom, naam: e.target.value})} className="flex-1 px-2 py-1 border rounded text-sm"/>
          <input required type="number" step="0.1" placeholder="D" value={custom.d} onChange={e => setCustom({...custom, d: e.target.value})} className="w-20 px-2 py-1 border rounded text-sm"/>
          <button type="submit" className="bg-orange-500 text-white px-3 py-1 rounded text-sm font-semibold">OK</button>
        </form>
      )}
    </div>
  );
}

function SprongInput({ toestel, onAdd }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/checker/suggest", { params: { query: q, toestel, limit: 10 } });
        setSuggestions(r.data);
        setHighlight(0);
      } catch { setSuggestions([]); }
    }, 150);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, toestel]);

  // Outside click → close
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (s) => {
    onAdd({ input: q || s.naam, naam: s.naam, d: s.d, fuzzy: false });
    setQ(""); setSuggestions([]); setOpen(false);
  };

  const addFallback = async () => {
    if (!q.trim()) return;
    try {
      const r = await api.get("/checker/match", { params: { query: q, toestel } });
      if (r.data && r.data.naam) onAdd({ input: q, naam: r.data.naam, d: r.data.d, fuzzy: r.data.fuzzy });
      else onAdd({ input: q, naam: null, d: null, error: "Niet herkend" });
    } catch { onAdd({ input: q, naam: null, d: null, error: "Niet herkend" }); }
    setQ(""); setSuggestions([]); setOpen(false);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") { e.preventDefault(); addFallback(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder="bv. 'dub hoek' of 'salto gestrekt 1/1' of 'barani'"
            className="w-full pl-9 pr-3 py-2.5 border border-input rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="sprong-input"
            autoComplete="off"
          />
        </div>
        <button onClick={addFallback} className="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded-lg flex items-center gap-2 font-semibold" data-testid="add-sprong-btn">
          <Plus size={16}/> Toevoegen
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="suggestions-dropdown">
          {suggestions.map((s, i) => (
            <li
              key={s.naam}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`flex items-center justify-between px-4 py-2.5 cursor-pointer border-b last:border-0 ${i === highlight ? "bg-orange-50" : "hover:bg-slate-50"}`}
              data-testid={`suggestion-${i}`}
            >
              <span className="font-medium text-slate-800">{s.naam}</span>
              <span className="font-mono text-sm text-slate-500">{s.d?.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ============================================================
// MICRO TEAMGYM CHECKER — 3 sprongbeurten × 3 turners
// Sprongbeurt 1 = gezamenlijk (1 sprong voor alle 3 turners)
// Sprongbeurt 2 & 3 = vrije keuze per turner (3 sprongen)
// MT: kies type per beurt (Trampoline / Pegasus); min 1 elk
// Airtrack: kies type per beurt (Voorover / Achterover / Keuze)
// ============================================================
function MicroChecker({
  leeftijd, setLeeftijd, niveau, setNiveau, toestel, setToestel,
  vereniging, setVereniging, team, setTeam, leeftijdOptions, niveauOptions, limits,
}) {
  const TYPE_OPTS_MT = [
    { v: "trampoline", l: "Minitrampoline" },
    { v: "pegasus", l: "Pegasus (springtoestel)" },
  ];
  const TYPE_OPTS_AIR = [
    { v: "voorover", l: "Voorover" },
    { v: "achterover", l: "Achterover" },
    { v: "keuze", l: "Keuze" },
  ];
  const typeOpts = toestel === "mt" ? TYPE_OPTS_MT : TYPE_OPTS_AIR;
  const defaultType = toestel === "mt" ? "trampoline" : "keuze";
  const microToestelOpts = MICRO_TOESTEL_OPTIONS;

  // Each serie: { type, sprongen: [{naam, d}, ...] }
  // Serie 1: 1 sprong (gezamenlijk); Serie 2&3: 3 sprongen (per turner)
  const initSeries = () => ([
    { type: toestel === "mt" ? "trampoline" : "voorover", sprongen: [] },
    { type: toestel === "mt" ? "pegasus" : "achterover", sprongen: [] },
    { type: toestel === "mt" ? "trampoline" : "keuze", sprongen: [] },
  ]);
  const [series, setSeries] = useState(initSeries);
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);

  // Reset series wanneer toestel verandert (MT ↔ Airtrack)
  useEffect(() => { setSeries(initSeries()); setResult(null); }, [toestel]);

  const updateSerieType = (idx, type) => {
    const next = [...series];
    const prev = next[idx];
    // Bij MT: wisselen tussen trampoline en pegasus → sprongen wissen (andere catalog)
    const clearSprongen = toestel === "mt" && prev.type !== type;
    next[idx] = { ...prev, type, ...(clearSprongen ? { sprongen: [] } : {}) };
    setSeries(next);
  };
  const setSharedSprong = (sprong) => {
    const next = [...series];
    next[0] = { ...next[0], sprongen: sprong ? [sprong] : [] };
    setSeries(next);
  };
  const setTurnerSprong = (sIdx, turnerIdx, sprong) => {
    const next = [...series];
    const list = [...(next[sIdx].sprongen || [])];
    while (list.length < 3) list.push(null);
    list[turnerIdx] = sprong;
    next[sIdx] = { ...next[sIdx], sprongen: list };
    setSeries(next);
  };

  const validate = async () => {
    setValidating(true);
    try {
      const payload = {
        leeftijd, niveau, toestel,
        series: series.map(s => ({
          type: s.type,
          sprongen: (s.sprongen || []).filter(Boolean).map(x => ({ naam: x.naam, d: x.d })),
        })),
      };
      const r = await api.post("/checker/validate-micro", payload);
      setResult(r.data);
    } catch (e) {
      setResult({ error: formatError(e) });
    } finally { setValidating(false); }
  };

  const downloadExcel = async () => {
    const payload = {
      toestel, vereniging, team, niveau, leeftijd,
      series: series.map(s => ({
        type: s.type,
        sprongen: (s.sprongen || []).filter(Boolean).map(x => ({ naam: x.naam, d: x.d })),
      })),
    };
    const r = await api.post("/excel/generate-micro", payload, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sprongformulier_micro_${toestel}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const payload = {
      toestel, vereniging, team, niveau, leeftijd,
      series: series.map(s => ({
        type: s.type,
        sprongen: (s.sprongen || []).filter(Boolean).map(x => ({ naam: x.naam, d: x.d })),
      })),
    };
    const r = await api.post("/pdf/generate-micro", payload, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sprongformulier_micro_${toestel}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter alleen Micro-leeftijden (niet Meso)
  const microLeeftijdOpts = leeftijdOptions.filter(o => o.v.startsWith("micro_"));

  return (
    <div data-testid="checker-micro">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Tool</div>
      <h1 className="heading-display text-4xl sm:text-5xl mt-1">Sprong Checker — Micro Teamgym</h1>
      <p className="text-slate-600 mt-3 mb-8">3 sprongbeurten van 3 turners. Sprongbeurt 1 is <strong>gezamenlijk</strong> (alle turners zelfde sprong). Sprongbeurt 2 & 3 zijn <strong>vrije keuze</strong> per turner. De som per sprongbeurt wordt gemiddeld over 3 beurten — dát getal moet binnen het min/max van het niveau vallen.</p>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="gs-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Leeftijd" value={leeftijd} onChange={setLeeftijd} opts={microLeeftijdOpts} testid="select-leeftijd"/>
            <Select label="Niveau" value={niveau} onChange={setNiveau} opts={niveauOptions.map(n => ({v:n,l:n}))} testid="select-niveau"/>
          </div>
          <Select label="Toestel" value={toestel} onChange={setToestel} opts={microToestelOpts} testid="select-toestel"/>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Vereniging" value={vereniging} onChange={setVereniging} testid="input-vereniging"/>
            <TextField label="Teamnamen" value={team} onChange={setTeam} testid="input-team"/>
          </div>
        </div>
        <div className="gs-card p-5 space-y-2 text-sm">
          <div className="font-semibold text-slate-700">Gemiddelde D over 3 sprongbeurten moet zijn:</div>
          <div className="font-mono font-bold text-lg" data-testid="limit-display">
            {limits.min != null && limits.max != null && `tussen ${limits.min} en ${limits.max}`}
            {limits.min != null && limits.max == null && `minimaal ${limits.min}`}
            {limits.min == null && limits.max != null && `maximaal ${limits.max}`}
            {limits.min == null && limits.max == null && "geen limiet voor dit niveau"}
          </div>
          {toestel === "mt" && (
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded text-xs">
              <strong className="text-orange-800">Samenstellingseis:</strong>
              <div className="text-orange-700 mt-1">Van de 3 sprongbeurten: <strong>minimaal 1 met pegasus</strong> én <strong>minimaal 1 zonder pegasus</strong>. Geldig: 1 pega + 2 trampo, of 2 pega + 1 trampo.</div>
            </div>
          )}
        </div>
      </div>

      {/* SERIES UI */}
      <div className="space-y-4 mb-6">
        {series.map((serie, sIdx) => {
          const isShared = sIdx === 0;
          return (
            <div key={sIdx} className="gs-card p-5" data-testid={`micro-serie-${sIdx}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-700 font-bold w-8 h-8 rounded-full flex items-center justify-center">{sIdx + 1}</span>
                  <h2 className="font-bold">Sprongbeurt {sIdx + 1} {isShared && <span className="text-xs font-normal text-orange-600 ml-1 inline-flex items-center gap-1"><Users size={12}/> Gezamenlijk</span>}</h2>
                </div>
                <div className="flex-1"/>
                <label className="text-sm flex items-center gap-2">
                  <span className="text-slate-600">Type:</span>
                  <select value={serie.type} onChange={(e) => updateSerieType(sIdx, e.target.value)} className="px-2 py-1 border rounded text-sm" data-testid={`serie-${sIdx}-type`}>
                    {typeOpts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </label>
              </div>

              {isShared ? (
                <div>
                  <div className="text-sm text-slate-600 mb-2">Alle 3 turners doen dezelfde sprong{toestel === "airtrack" ? "-serie" : ""}:</div>
                  {toestel === "airtrack" ? (
                    <MicroAirtrackSeriePicker
                      serieType={serie.type}
                      value={serie.sprongen?.[0] || null}
                      onSet={setSharedSprong}
                      testid={`serie-0-shared`}
                    />
                  ) : (
                    <MicroSprongPicker
                      toestel={toestel}
                      serieType={serie.type}
                      value={serie.sprongen?.[0] || null}
                      onSet={setSharedSprong}
                      testid={`serie-0-shared`}
                    />
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  {["A", "B", "C"].map((label, ti) => (
                    <div key={ti}>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Turner {label}</div>
                      {toestel === "airtrack" ? (
                        <MicroAirtrackSeriePicker
                          serieType={serie.type}
                          value={serie.sprongen?.[ti] || null}
                          onSet={(sp) => setTurnerSprong(sIdx, ti, sp)}
                          testid={`serie-${sIdx}-turner-${ti}`}
                        />
                      ) : (
                        <MicroSprongPicker
                          toestel={toestel}
                          serieType={serie.type}
                          value={serie.sprongen?.[ti] || null}
                          onSet={(sp) => setTurnerSprong(sIdx, ti, sp)}
                          testid={`serie-${sIdx}-turner-${ti}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={validate} disabled={validating} className="bg-secondary text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-slate-800" data-testid="validate-btn">
          {validating ? "Controleren..." : "Controleer"}
        </button>
        <button onClick={downloadExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2" data-testid="excel-download-btn">
          <Download size={16}/> Excel sprongformulier
        </button>
        <button onClick={downloadPdf} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2" data-testid="pdf-download-btn">
          <Download size={16}/> PDF download
        </button>
      </div>

      {result && !result.error && (
        <div className="gs-card p-5" data-testid="validation-result">
          <div className="flex items-center gap-3 mb-4">
            {result.all_ok ? (
              <><CheckCircle2 className="text-emerald-600" size={28}/><div><div className="font-bold text-emerald-700">Voldoet aan {niveau}-niveau!</div><div className="text-sm text-slate-600">Gemiddelde D-waarde: {result.avg_d}</div></div></>
            ) : (
              <><AlertTriangle className="text-amber-600" size={28}/><div><div className="font-bold text-amber-700">Voldoet niet aan {niveau}-niveau</div><div className="text-sm text-slate-600">Zie details hieronder</div></div></>
            )}
          </div>

          {/* Gemiddelde berekening */}
          <div className={`mb-4 p-3 rounded-lg border ${result.avg_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="avg-result">
            <div className="font-semibold text-sm">Gemiddelde over 3 sprongbeurten</div>
            <div className="font-mono text-lg mt-1">D = {result.avg_d}</div>
            <div className="text-xs text-slate-600 mt-1">{result.avg_reason}</div>
          </div>

          {toestel === "mt" && (
            <div className={`mb-4 p-3 rounded-lg border ${result.composition_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              <span className="font-semibold">Samenstelling: </span><span>{result.composition_reason}</span>
            </div>
          )}

          {/* Per-serie sommen */}
          <div className="space-y-2">
            {result.series.map((s) => (
              <div key={s.index} className={`p-3 rounded-lg border ${s.ok ? "bg-slate-50 border-slate-200" : "bg-red-50 border-red-200"}`} data-testid={`result-serie-${s.index}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="font-semibold">Sprongbeurt {s.index} <span className="text-xs font-normal text-slate-500 ml-1">({s.type}{s.is_shared ? " · gezamenlijk" : ""})</span></span>
                  <span className="font-mono text-sm font-bold">Som = {s.team_total}</span>
                </div>
                <div className="text-xs text-slate-600">
                  {s.turners.map((t, i) => (
                    <span key={i} className="inline-block mr-3">Turner {"ABC"[i]}: {t.naam} <span className="font-mono text-slate-800">({t.d?.toFixed(1)})</span></span>
                  ))}
                </div>
                <div className={`text-xs mt-1 ${s.ok ? "text-slate-600" : "text-red-700"}`}>{s.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {result?.error && <div className="text-red-600">{result.error}</div>}
    </div>
  );
}

// Picker voor één sprong (in Micro). Toont autocomplete dropdown + handmatige invoer.
// Voor MT: serieType=trampoline → MICRO_MT catalog; serieType=pegasus → MICRO_PEGASUS catalog.
// Voor airtrack: gebruikt MICRO_AIRTRACK catalog.
function MicroSprongPicker({ toestel, serieType, value, onSet, testid, onderdeel = "micro_meso" }) {
  const [q, setQ] = useState(value?.naam || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQ(value?.naam || ""); }, [value?.naam]);

  // Bepaal welk toestel-keyword we naar de API sturen op basis van serie type
  const queryToestel = useMemo(() => {
    if (toestel === "airtrack" || toestel === "pegasus") return toestel;
    return serieType === "pegasus" ? "pegasus" : "mt";
  }, [toestel, serieType]);

  useEffect(() => {
    if (!q.trim() || (value && q === value.naam)) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/checker/suggest", { params: { query: q, toestel: queryToestel, limit: 8, onderdeel } });
        setSuggestions(r.data);
      } catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [q, queryToestel, onderdeel]);

  const pick = (s) => {
    onSet({ naam: s.naam, d: s.d });
    setQ(s.naam);
    setSuggestions([]);
    setOpen(false);
  };

  const clear = () => { onSet(null); setQ(""); setSuggestions([]); };

  return (
    <div className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Typ sprong..."
          className={`w-full pl-9 pr-8 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 ${value ? "border-emerald-300 bg-emerald-50/50" : ""}`}
          autoComplete="off"
        />
        {value && (
          <button onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-600" type="button">
            <X size={14}/>
          </button>
        )}
      </div>
      {value && (
        <div className="text-xs text-emerald-700 mt-0.5 font-medium">D = {value.d?.toFixed(1)}</div>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <li key={s.naam} onMouseDown={(e) => { e.preventDefault(); pick(s); }} className="flex justify-between px-3 py-2 cursor-pointer hover:bg-orange-50 border-b last:border-0 text-sm">
              <span>{s.naam}</span><span className="font-mono text-slate-500">{s.d?.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// AIRTRACK SERIE PICKER — meerdere elementen in één sprongbeurt
// Aggregeert een lijst elementen naar één "sprong" met naam="elem1 + elem2" en d=som.
// Gebruikt door Micro en Groepspringen Team-modus op Airtrack.
// ============================================================
function MicroAirtrackSeriePicker({ serieType, value, onSet, testid, onderdeel = "micro_meso" }) {
  const elements = value?.elements || [];
  const [showPicker, setShowPicker] = useState(false);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/checker/suggest", { params: { query: q, toestel: "airtrack", limit: 8, onderdeel } });
        setSuggestions(r.data);
      } catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [q, onderdeel]);

  const commit = (nextElements) => {
    if (nextElements.length === 0) { onSet(null); return; }
    const totalD = nextElements.reduce((s, x) => s + (x.d || 0), 0);
    const naam = nextElements.map(e => e.naam).join(" + ");
    onSet({ naam, d: parseFloat(totalD.toFixed(2)), elements: nextElements });
  };

  const addElement = (s) => {
    const next = [...elements, { naam: s.naam, d: s.d }];
    commit(next);
    setQ("");
    setSuggestions([]);
    setOpen(false);
    setShowPicker(false);
  };

  const removeElement = (idx) => {
    const next = elements.filter((_, i) => i !== idx);
    commit(next);
  };

  const totalD = elements.reduce((s, x) => s + (x.d || 0), 0);

  return (
    <div className="space-y-1.5" data-testid={testid}>
      {elements.length > 0 && (
        <ul className="space-y-1">
          {elements.map((el, i) => (
            <li key={i} className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-200 rounded px-2 py-1 text-xs" data-testid={`${testid}-el-${i}`}>
              <span className="flex-1 truncate" title={el.naam}>{el.naam}</span>
              <span className="font-mono text-slate-600">{el.d?.toFixed(1)}</span>
              <button onClick={() => removeElement(i)} className="text-slate-400 hover:text-red-600" type="button"><X size={12}/></button>
            </li>
          ))}
        </ul>
      )}
      {elements.length > 0 && (
        <div className="text-xs text-emerald-700 font-semibold">D-som = {totalD.toFixed(1)} <span className="text-slate-400 font-normal">({elements.length} elem.)</span></div>
      )}
      {!showPicker ? (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="w-full text-xs border-2 border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 text-orange-700 rounded py-1.5 font-semibold flex items-center justify-center gap-1"
          data-testid={`${testid}-add-btn`}
        >
          <Plus size={12}/> Element toevoegen
        </button>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => { setOpen(false); if (!q) setShowPicker(false); }, 200)}
              autoFocus
              placeholder="Typ element..."
              className="w-full pl-7 pr-2 py-1.5 text-xs border rounded bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              autoComplete="off"
              data-testid={`${testid}-input`}
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-52 overflow-y-auto">
              {suggestions.map((s) => (
                <li key={s.naam} onMouseDown={(e) => { e.preventDefault(); addElement(s); }} className="flex justify-between px-2.5 py-1.5 cursor-pointer hover:bg-orange-50 border-b last:border-0 text-xs">
                  <span>{s.naam}</span><span className="font-mono text-slate-500">{s.d?.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


// Beurten 1-6 = gezamenlijk (1 sprong voor alle 6 turners)
// Beurten 7-8 = keuze (6 sprongen, 1 per turner)
// D-score = som gezamenlijke beurten + avg keuze 1 + avg keuze 2
// ============================================================
function GroepspringenTeamChecker({
  leeftijd, setLeeftijd, niveau, setNiveau, toestel, setToestel,
  vereniging, setVereniging, team, setTeam,
  leeftijdOptions, niveauOptions, limits,
}) {
  const TYPE_OPTS_AIR = [
    { v: "voorover", l: "Voorover" },
    { v: "achterover", l: "Achterover" },
    { v: "keuze", l: "Keuze" },
  ];

  const initSeries = () => {
    const sharedCount = toestel === "airtrack" ? 4 : 6;
    const shared = Array.from({ length: sharedCount }, () => ({
      type: toestel === "airtrack" ? "voorover" : "",
      sprongen: [],
    }));
    const choice = Array.from({ length: 2 }, () => ({
      type: toestel === "airtrack" ? "keuze" : "",
      sprongen: [],
    }));
    return [...shared, ...choice];
  };
  const [series, setSeries] = useState(initSeries);
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const sharedCount = toestel === "airtrack" ? 4 : 6;

  useEffect(() => { setSeries(initSeries()); setResult(null); }, [toestel]);

  const updateType = (idx, type) => {
    const next = [...series];
    next[idx] = { ...next[idx], type };
    setSeries(next);
  };
  const setShared = (idx, sprong) => {
    const next = [...series];
    next[idx] = { ...next[idx], sprongen: sprong ? [sprong] : [] };
    setSeries(next);
  };
  const setTurner = (sIdx, tIdx, sprong) => {
    const next = [...series];
    const list = [...(next[sIdx].sprongen || [])];
    while (list.length < 6) list.push(null);
    list[tIdx] = sprong;
    next[sIdx] = { ...next[sIdx], sprongen: list };
    setSeries(next);
  };

  const validate = async () => {
    setValidating(true);
    try {
      const payload = {
        leeftijd, niveau, toestel,
        series: series.map(s => ({
          type: s.type,
          sprongen: (s.sprongen || []).filter(Boolean).map(x => ({ naam: x.naam, d: x.d })),
        })),
      };
      const r = await api.post("/checker/validate-groepspringen-team", payload);
      setResult(r.data);
    } catch (e) { setResult({ error: formatError(e) }); }
    finally { setValidating(false); }
  };

  const showType = toestel === "airtrack";

  return (
    <div data-testid="checker-groepspringen-team">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Team-modus</div>
          <h1 className="heading-display text-4xl sm:text-5xl mt-1">Sprong Checker — Groepspringen</h1>
        </div>
      </div>
      <p className="text-slate-600 mt-3 mb-6">{sharedCount} gezamenlijke sprongbeurten + 2 keuze-beurten van 6 turners. Totaal-D = som gezamenlijk + gemiddelde keuze 1 + gemiddelde keuze 2.</p>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="gs-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Leeftijd" value={leeftijd} onChange={setLeeftijd} opts={leeftijdOptions} testid="select-leeftijd"/>
            <Select label="Niveau" value={niveau} onChange={setNiveau} opts={niveauOptions.map(n => ({v:n,l:n}))} testid="select-niveau"/>
          </div>
          <Select label="Toestel" value={toestel} onChange={setToestel} opts={TOESTEL_OPTIONS} testid="select-toestel"/>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Vereniging" value={vereniging} onChange={setVereniging} testid="input-vereniging"/>
            <TextField label="Teamnamen" value={team} onChange={setTeam} testid="input-team"/>
          </div>
        </div>
        <div className="gs-card p-5 space-y-2 text-sm">
          <div className="font-semibold text-slate-700">Totaal-D moet zijn:</div>
          <div className="font-mono font-bold text-lg" data-testid="limit-display">
            {limits.geen_max && "Geen maximum"}
            {!limits.geen_max && limits.min != null && limits.max != null && `tussen ${limits.min} en ${limits.max}`}
            {!limits.geen_max && limits.min != null && limits.max == null && `minimaal ${limits.min}`}
            {!limits.geen_max && limits.min == null && limits.max != null && `maximaal ${limits.max}`}
            {!limits.geen_max && limits.min == null && limits.max == null && "geen limiet"}
          </div>
          <div className="text-xs text-slate-600 mt-2">D = som {sharedCount} gezamenlijke beurten + gemiddelde keuze-beurt 1 + gemiddelde keuze-beurt 2</div>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        {series.map((serie, sIdx) => {
          const isShared = sIdx < sharedCount;
          return (
            <div key={sIdx} className="gs-card p-5" data-testid={`gsteam-serie-${sIdx}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className={`${isShared ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"} font-bold w-8 h-8 rounded-full flex items-center justify-center`}>{sIdx + 1}</span>
                <h2 className="font-bold">Sprongbeurt {sIdx + 1} <span className="text-xs font-normal text-slate-500 ml-1 inline-flex items-center gap-1"><Users size={12}/> {isShared ? "Gezamenlijk (6 turners)" : "Keuze (per turner)"}</span></h2>
                <div className="flex-1"/>
                {showType && (
                  <label className="text-sm flex items-center gap-2">
                    <span className="text-slate-600">Type:</span>
                    <select value={serie.type} onChange={(e) => updateType(sIdx, e.target.value)} className="px-2 py-1 border rounded text-sm" data-testid={`gsteam-type-${sIdx}`}>
                      {TYPE_OPTS_AIR.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {isShared ? (
                toestel === "airtrack" ? (
                  <MicroAirtrackSeriePicker onderdeel="groepspringen" serieType={serie.type} value={serie.sprongen?.[0] || null} onSet={(sp) => setShared(sIdx, sp)} testid={`gsteam-shared-${sIdx}`}/>
                ) : (
                  <MicroSprongPicker onderdeel="groepspringen" toestel={toestel} serieType={serie.type} value={serie.sprongen?.[0] || null} onSet={(sp) => setShared(sIdx, sp)} testid={`gsteam-shared-${sIdx}`}/>
                )
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {Array.from({ length: 6 }).map((_, ti) => (
                    <div key={ti}>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Turner {ti + 1}</div>
                      {toestel === "airtrack" ? (
                        <MicroAirtrackSeriePicker onderdeel="groepspringen" serieType={serie.type} value={serie.sprongen?.[ti] || null} onSet={(sp) => setTurner(sIdx, ti, sp)} testid={`gsteam-turner-${sIdx}-${ti}`}/>
                      ) : (
                        <MicroSprongPicker onderdeel="groepspringen" toestel={toestel} serieType={serie.type} value={serie.sprongen?.[ti] || null} onSet={(sp) => setTurner(sIdx, ti, sp)} testid={`gsteam-turner-${sIdx}-${ti}`}/>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mb-6">
        <button onClick={validate} disabled={validating} className="bg-secondary text-white px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-slate-800" data-testid="validate-btn">
          {validating ? "Controleren..." : "Controleer team-routine"}
        </button>
      </div>

      {result && !result.error && (
        <div className="gs-card p-5" data-testid="validation-result">
          <div className="flex items-center gap-3 mb-4">
            {result.all_ok ? (
              <><CheckCircle2 className="text-emerald-600" size={28}/><div><div className="font-bold text-emerald-700">Voldoet aan {niveau}-niveau</div><div className="text-sm text-slate-600">Totaal-D: {result.total_d}</div></div></>
            ) : (
              <><AlertTriangle className="text-amber-600" size={28}/><div><div className="font-bold text-amber-700">Voldoet niet</div><div className="text-sm text-slate-600">Zie hieronder</div></div></>
            )}
          </div>
          <div className={`mb-4 p-3 rounded-lg border ${result.all_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className="font-mono text-sm">
              Som gezamenlijk = <strong>{result.shared_total}</strong>
              {result.choice_avgs.map((a, i) => (<span key={i}> + gem. keuze {i+1} = <strong>{a}</strong></span>))}
              {' '}= <strong>{result.total_d}</strong>
            </div>
            <div className="text-xs mt-1">{result.reason}</div>
          </div>
          <div className="space-y-2">
            {result.series.map((s) => (
              <div key={s.index} className={`p-3 rounded-lg border text-sm ${s.ok ? "bg-slate-50 border-slate-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">Sprongbeurt {s.index} <span className="text-xs font-normal text-slate-500 ml-1">({s.is_shared ? "gezamenlijk" : "keuze"}{s.type ? ` · ${s.type}` : ""})</span></span>
                  <span className="font-mono text-xs">{s.is_shared ? `D = ${s.d}` : `Som ${s.team_sum} → gem ${s.d}`}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">{s.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {result?.error && <div className="text-red-600">{result.error}</div>}
    </div>
  );
}

